import { Check, Copy } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { convertAll, UNITS } from "../lib/datasize";

const SAMPLE = "1";

const selectClass =
  "h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/** Locale-independent number formatting (deterministic for SSR). */
function fmt(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(6).replace(/\.?0+$/, "");
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      aria-label={`${m.ds_copy()} ${label}`}
      className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
    >
      {copied ? (
        <Check className="h-4 w-4 text-emerald-500" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </button>
  );
}

export function DataSizeTool() {
  const uid = useId();
  const [value, setValue] = useState(SAMPLE);
  const [from, setFrom] = useState("MB");

  const result = useMemo(() => {
    if (value.trim() === "") return null;
    return convertAll(Number(value), from);
  }, [value, from]);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 rounded-2xl border border-border bg-card/60 p-6 sm:grid-cols-2 sm:p-8">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-val`}>{m.ds_value_label()}</Label>
          <Input
            id={`${uid}-val`}
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={m.ds_placeholder()}
            autoComplete="off"
            className="font-mono text-lg"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-from`}>{m.ds_from_label()}</Label>
          <select
            id={`${uid}-from`}
            className={selectClass}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          >
            {UNITS.map((u) => (
              <option key={u.id} value={u.id}>
                {u.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-3 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <h2 className="font-display text-lg font-bold tracking-tight">
          {m.ds_result_label()}
        </h2>
        {result ? (
          <div className="space-y-2">
            {result.map((u) => (
              <div
                key={u.id}
                className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card/40 px-4 py-2.5"
              >
                <span className="text-sm text-muted-foreground">{u.label}</span>
                <div className="flex items-center gap-3">
                  <span
                    data-testid={`ds-${u.id}`}
                    className="break-all text-right font-mono text-sm"
                  >
                    {fmt(u.value)}
                  </span>
                  <CopyButton value={fmt(u.value)} label={u.label} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p data-testid="ds-error" className="text-sm text-muted-foreground">
            {m.ds_invalid()}
          </p>
        )}
      </div>
    </div>
  );
}
